import { Controller } from "@hotwired/stimulus";
import QrScanner from "qr-scanner";

const QTAG_PATTERN = /^[A-Z]\d{6}$/;

// Connects to data-controller="qr-scanner"
//
// Owns the camera lifecycle for the Qtag-scan overlay. The overlay itself is
// a Common::Dialog (server-rendered, or built by the shared
// renderQrScannerField() JS template for the two JS-driven forms) — its
// open/close/Escape/backdrop dismissal is entirely owned by the unmodified
// dialog_controller.js, reached here through a Stimulus outlet. Two
// controllers nested together, not merged into one: this one never touches
// dialog chrome, and dialog_controller.js never touches the camera.
export default class extends Controller {
  static targets = ["input", "video", "errorMessage"];
  static outlets = ["dialog"];

  disconnect() {
    this.#destroyScanner();
  }

  start(event) {
    if (event) event.preventDefault();
    this.errorMessageTarget.classList.add("hidden");
    this.errorMessageTarget.textContent = "";
    this.dialogOutlet.open();

    this.#scanner = new QrScanner(
      this.videoTarget,
      (result) => this.#onScanSuccess(result),
      {
        preferredCamera: "environment",
        highlightScanRegion: true,
        highlightCodeOutline: true,
      }
    );

    this.#scanner.start().catch((err) => {
      this.#showError(
        err?.message?.includes("permission")
          ? "Camera permission denied. Please allow camera access and try again."
          : "Unable to start camera. Please enter the QTAG reference manually."
      );
    });
  }

  // Bound to the dialog's own "dialog:closed" event (Escape, backdrop click,
  // the header close button) so the camera always stops, however the dialog
  // was dismissed — this never closes the dialog itself, only the reverse.
  stopScanning() {
    this.#destroyScanner();
  }

  #onScanSuccess(result) {
    const reference = this.#extractReference(result.data);

    if (!QTAG_PATTERN.test(reference)) {
      this.#showError(
        `Could not read a valid QTAG reference from this code. Expected format: A000001`
      );
      return;
    }

    this.inputTarget.value = reference;
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
    this.#destroyScanner();
    this.dialogOutlet.close();
  }

  #extractReference(scanned) {
    try {
      const url = new URL(scanned);
      const segments = url.pathname.split("/").filter(Boolean);
      return segments.pop() ?? scanned;
    } catch {
      // Not a URL — use the raw scanned value
      return scanned.trim();
    }
  }

  #showError(message) {
    this.errorMessageTarget.textContent = message;
    this.errorMessageTarget.classList.remove("hidden");
  }

  #destroyScanner() {
    if (this.#scanner) {
      this.#scanner.stop();
      this.#scanner.destroy();
      this.#scanner = null;
    }
  }

  #scanner = null;
}
